<script setup lang="ts">
import {type Component, computed, type PropType} from 'vue';
import {LoaderCircle} from 'lucide-vue-next';

const props = defineProps({
    variant : {type: String as PropType<'primary' | 'ghost' | 'text' | 'danger'>, default: 'ghost'},
    icon    : {type: [Object, Function] as PropType<Component>, required: false}, // a Lucide icon is a functional component
    disabled: {type: Boolean, default: false},
    loading : {type: Boolean, default: false},
});
const emit = defineEmits<{ click: [event: MouseEvent] }>();

const VARIANT_CLASS: Record<string, string> = {
    primary: 'bg-zzc-blauw text-white border-transparent hover:bg-[#22365a]',
    ghost  : 'bg-white text-zzc-antraciet border-zzc-rand hover:border-zzc-muted',
    text   : 'bg-transparent text-zzc-blauw border-transparent hover:bg-white',
    danger : 'bg-white text-zzc-danger border-zzc-danger hover:bg-[var(--zzc-tag-bad-bg)]',
};

const classes = computed<string>(() => VARIANT_CLASS[props.variant]);

function onClick(event: MouseEvent) {
    if (!props.disabled && !props.loading) {
        emit('click', event);
    }
}
</script>

<template>
    <button
        type="button"
        class="inline-flex items-center gap-2 rounded-control border px-3 py-1.5 text-sm font-medium cursor-pointer disabled:opacity-40 disabled:cursor-default transition-colors"
        :class="classes"
        :disabled="disabled || loading"
        @click="onClick"
    >
        <LoaderCircle v-if="loading" :size="16" :stroke-width="2" class="animate-spin"/>
        <component v-else-if="icon" :is="icon" :size="16" :stroke-width="2"/>
        <slot/>
    </button>
</template>
